import { useEffect, useRef, useState, type FormEvent } from "react";
import type { LookupItem, PublicLeadRequest } from "@fa/shared";
import {
  getPublicGrades,
  getPublicSchools,
  PublicApiError,
  submitPublicLead,
} from "@/api/publicApi";
import { CAPTCHA_PROVIDER, getCaptchaToken } from "@/lib/captcha";
import { captureTrackingParams } from "@/lib/utm";
import { BRAND, whatsappLink } from "@/lib/brand";
import { t as getFixedT } from "@/i18n";
import type { SupportedLanguage } from "@/lib/site";
import {
  FIELD_ORDER,
  MESSAGE_MAX_LENGTH,
  isPhoneFormatLikelyValid,
  validateAll,
  validateField,
  type LeadFormFieldName,
  type LeadFormValues,
} from "@/lib/leadFormValidation";
import { FormField } from "@/components/FormField";
import "./LeadForm.css";

type LookupStatus = "loading" | "loaded" | "error";

interface BannerError {
  kind: "rate-limit" | "captcha" | "network" | "generic";
  message: string;
}

const INITIAL_VALUES: LeadFormValues = {
  fullName: "",
  phone: "",
  email: "",
  gradeInterestedId: "",
  schoolInterestedId: "",
  message: "",
  consent: false,
};

/** Real submit-time honeypot state is kept separate from `LeadFormValues` — it's never rendered
 * through `FormField`/validated like a real field, only ever read at submit time. */
type HoneypotValue = string;

interface LeadFormProps {
  lang: SupportedLanguage;
}

/**
 * The lead-capture form — 09-landing-page.md §3, the page's single conversion point. Prerendered
 * as real markup (SEO/no-JS baseline shows the full form); the lookup selects and submit behavior
 * hydrate client-side.
 */
export function LeadForm({ lang }: LeadFormProps) {
  const t = getFixedT(lang);

  const [grades, setGrades] = useState<LookupItem[]>([]);
  const [gradesStatus, setGradesStatus] = useState<LookupStatus>("loading");
  const [schools, setSchools] = useState<LookupItem[]>([]);
  const [schoolsStatus, setSchoolsStatus] = useState<LookupStatus>("loading");

  const [values, setValues] = useState<LeadFormValues>(INITIAL_VALUES);
  const [honeypot, setHoneypot] = useState<HoneypotValue>("");
  const [errors, setErrors] = useState<
    Partial<Record<LeadFormFieldName, string>>
  >({});
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "submitting" | "success"
  >("idle");
  const [banner, setBanner] = useState<BannerError | null>(null);

  const fieldRefs = useRef<
    Partial<Record<LeadFormFieldName, HTMLElement | null>>
  >({});
  const successRef = useRef<HTMLDivElement | null>(null);
  const formTopRef = useRef<HTMLDivElement | null>(null);

  const loadGrades = () => {
    setGradesStatus("loading");
    getPublicGrades()
      .then((data) => {
        setGrades(data);
        setGradesStatus("loaded");
      })
      .catch(() => setGradesStatus("error"));
  };

  const loadSchools = () => {
    setSchoolsStatus("loading");
    getPublicSchools()
      .then((data) => {
        setSchools(data);
        setSchoolsStatus("loaded");
      })
      .catch(() => setSchoolsStatus("error"));
  };

  useEffect(() => {
    loadGrades();
    loadSchools();
    // Mount-only fetch — `loadGrades`/`loadSchools` are stable (not memoized, but only ever called
    // here and from their own retry buttons); re-running this effect on every render would refetch
    // needlessly.
  }, []);

  // (Removed the desktop on-mount autofocus of the first field — focusing a field partway down the
  // page scrolled the viewport straight to the form, so visitors never saw the top/hero. The page
  // now starts at the top; the field still focuses normally on click/tab.)

  useEffect(() => {
    if (submitStatus === "success") {
      successRef.current?.focus();
    }
  }, [submitStatus]);

  const gradesAvailable = gradesStatus === "loaded" && grades.length > 0;

  function setField<K extends keyof LeadFormValues>(
    name: K,
    value: LeadFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [name]: value }));
  }

  function handleBlur(name: LeadFormFieldName) {
    const error = validateField(name, values, gradesAvailable);
    setErrors((prev) => ({ ...prev, [name]: error ?? undefined }));
  }

  function clearFieldError(name: LeadFormFieldName) {
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitStatus === "submitting") return;

    const { errors: newErrors, firstInvalid } = validateAll(
      values,
      gradesAvailable,
    );
    setErrors(newErrors);
    setBanner(null);

    if (firstInvalid) {
      fieldRefs.current[firstInvalid]?.focus();
      return;
    }
    if (!gradesAvailable) {
      // Required field with nothing to select — nothing more the visitor can do client-side.
      fieldRefs.current.gradeInterestedId?.focus();
      return;
    }

    setSubmitStatus("submitting");
    try {
      const captchaToken = await getCaptchaToken();
      const tracking = captureTrackingParams(`/${lang}`);
      const payload: PublicLeadRequest = {
        fullName: values.fullName.trim(),
        phone: values.phone.trim(),
        email: values.email.trim() || null,
        gradeInterestedId: Number(values.gradeInterestedId),
        schoolInterestedId: values.schoolInterestedId
          ? Number(values.schoolInterestedId)
          : null,
        message: values.message.trim() || null,
        consent: values.consent,
        captchaToken,
        sourcePage: tracking.sourcePage,
        utmSource: tracking.utmSource,
        utmMedium: tracking.utmMedium,
        utmCampaign: tracking.utmCampaign,
        // Honeypot — real visitors never fill this; a bot that does gets a silent server no-op
        // (§3.1 "client behaves identically to success"), so we never branch on it client-side.
        website: honeypot || undefined,
      };
      const ack = await submitPublicLead(payload);
      if (ack.success) {
        setSubmitStatus("success");
      } else {
        setSubmitStatus("idle");
        setBanner({
          kind: "generic",
          message: ack.message || t("leadForm.errors.generic"),
        });
      }
    } catch (err) {
      setSubmitStatus("idle");
      handleSubmitError(err);
    }
  }

  function handleSubmitError(err: unknown) {
    if (!(err instanceof PublicApiError)) {
      setBanner({ kind: "network", message: t("leadForm.errors.network") });
      return;
    }
    if (err.status === 429 || err.code === "RATE_LIMITED") {
      setBanner({
        kind: "rate-limit",
        message: t("leadForm.errors.rateLimited", {
          phone: BRAND.phoneDisplay,
        }),
      });
      return;
    }
    if (err.code === "CAPTCHA_FAILED") {
      setBanner({
        kind: "captcha",
        message: t("leadForm.errors.captchaFailed"),
      });
      return;
    }
    if (err.status === null) {
      setBanner({ kind: "network", message: t("leadForm.errors.network") });
      return;
    }
    if (err.details.length > 0) {
      const fieldErrors: Partial<Record<LeadFormFieldName, string>> = {};
      let firstField: LeadFormFieldName | null = null;
      for (const detail of err.details) {
        const field = detail.field as LeadFormFieldName | undefined;
        if (!field || !FIELD_ORDER.includes(field)) continue;
        const message =
          err.code === "LOOKUP_VALUE_INACTIVE"
            ? t("leadForm.errors.lookupInactive")
            : detail.message;
        fieldErrors[field] = message;
        firstField ??= field;
      }
      if (firstField) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        fieldRefs.current[firstField]?.focus();
        return;
      }
    }
    setBanner({ kind: "generic", message: t("leadForm.errors.generic") });
  }

  if (submitStatus === "success") {
    return (
      <div
        ref={successRef}
        className="fa-lead-form__success"
        role="status"
        tabIndex={-1}
        data-testid="lead-form-success"
      >
        <p className="fa-lead-form__success-icon" aria-hidden="true">
          ✓
        </p>
        <h3>{t("leadForm.success.heading")}</h3>
        <p>{t("leadForm.success.body")}</p>
        <a
          className="fa-btn fa-btn--secondary"
          href={whatsappLink(t("leadForm.success.whatsappMessage"))}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("leadForm.success.whatsappLink")}
        </a>
      </div>
    );
  }

  const submitting = submitStatus === "submitting";

  return (
    <div ref={formTopRef}>
      {banner && (
        <div
          className={`fa-lead-form__banner fa-lead-form__banner--${banner.kind}`}
          role="alert"
        >
          <p>{banner.message}</p>
          {(banner.kind === "rate-limit" ||
            banner.kind === "captcha" ||
            banner.kind === "network") && (
            <a
              className="fa-lead-form__banner-phone"
              href={`tel:${BRAND.phoneE164}`}
            >
              <bdi>{BRAND.phoneDisplay}</bdi>
            </a>
          )}
        </div>
      )}

      <form noValidate onSubmit={handleSubmit} className="fa-lead-form">
        <p className="fa-required-legend">{t("leadForm.requiredLegend")}</p>

        <div className="fa-field-row">
          <FormField
            id="lead-fullName"
            label={t("leadForm.fields.fullName")}
            required
            error={errors.fullName ? t(errors.fullName) : undefined}
          >
            <input
              ref={(el) => {
                fieldRefs.current.fullName = el;
              }}
              type="text"
              name="fullName"
              autoComplete="name"
              value={values.fullName}
              disabled={submitting}
              maxLength={150}
              onChange={(e) => {
                setField("fullName", e.target.value);
                clearFieldError("fullName");
              }}
              onBlur={() => handleBlur("fullName")}
            />
          </FormField>

          <FormField
            id="lead-phone"
            label={t("leadForm.fields.phone")}
            required
            error={errors.phone ? t(errors.phone) : undefined}
            warning={
              !errors.phone &&
              values.phone.trim() &&
              !isPhoneFormatLikelyValid(values.phone)
                ? t("leadForm.fields.phoneWarning")
                : undefined
            }
          >
            <input
              ref={(el) => {
                fieldRefs.current.phone = el;
              }}
              type="tel"
              name="phone"
              inputMode="tel"
              autoComplete="tel"
              placeholder="01xxxxxxxxx"
              value={values.phone}
              disabled={submitting}
              maxLength={20}
              onChange={(e) => {
                setField("phone", e.target.value);
                clearFieldError("phone");
              }}
              onBlur={() => handleBlur("phone")}
            />
          </FormField>
        </div>

        <FormField
          id="lead-email"
          label={t("leadForm.fields.email")}
          error={errors.email ? t(errors.email) : undefined}
        >
          <input
            ref={(el) => {
              fieldRefs.current.email = el;
            }}
            type="email"
            name="email"
            inputMode="email"
            autoComplete="email"
            value={values.email}
            disabled={submitting}
            onChange={(e) => {
              setField("email", e.target.value);
              clearFieldError("email");
            }}
            onBlur={() => handleBlur("email")}
          />
        </FormField>

        <div className="fa-field-row">
          <FormField
            id="lead-grade"
            label={t("leadForm.fields.grade")}
            required
            error={
              errors.gradeInterestedId
                ? t(errors.gradeInterestedId)
                : gradesStatus === "error"
                  ? t("leadForm.errors.gradesLoadError")
                  : gradesStatus === "loaded" && grades.length === 0
                    ? t("leadForm.fields.gradeEmpty")
                    : undefined
            }
          >
            <select
              ref={(el) => {
                fieldRefs.current.gradeInterestedId = el;
              }}
              name="gradeInterestedId"
              value={values.gradeInterestedId}
              disabled={
                submitting || gradesStatus !== "loaded" || grades.length === 0
              }
              onChange={(e) => {
                setField("gradeInterestedId", e.target.value);
                clearFieldError("gradeInterestedId");
              }}
              onBlur={() => handleBlur("gradeInterestedId")}
            >
              <option value="">
                {gradesStatus === "loading"
                  ? t("leadForm.fields.gradeLoading")
                  : t("leadForm.fields.gradePlaceholder")}
              </option>
              {grades.map((grade) => (
                <option key={grade.id} value={grade.id}>
                  {lang === "ar" ? grade.labelAr : grade.labelEn}
                </option>
              ))}
            </select>
            {gradesStatus === "error" && (
              <button
                type="button"
                className="fa-btn fa-btn--ghost fa-lead-form__retry"
                onClick={loadGrades}
              >
                {t("leadForm.retry")}
              </button>
            )}
          </FormField>

          <FormField
            id="lead-school"
            label={t("leadForm.fields.school")}
            hint={
              schoolsStatus === "error"
                ? t("leadForm.errors.schoolsLoadError")
                : undefined
            }
          >
            <select
              ref={(el) => {
                fieldRefs.current.schoolInterestedId = el;
              }}
              name="schoolInterestedId"
              value={values.schoolInterestedId}
              disabled={
                submitting || schoolsStatus !== "loaded" || schools.length === 0
              }
              onChange={(e) => setField("schoolInterestedId", e.target.value)}
            >
              <option value="">
                {schoolsStatus === "loading"
                  ? t("leadForm.fields.schoolLoading")
                  : t("leadForm.fields.schoolPlaceholder")}
              </option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>
                  {lang === "ar" ? school.labelAr : school.labelEn}
                </option>
              ))}
            </select>
            {schoolsStatus === "error" && (
              <button
                type="button"
                className="fa-btn fa-btn--ghost fa-lead-form__retry"
                onClick={loadSchools}
              >
                {t("leadForm.retry")}
              </button>
            )}
          </FormField>
        </div>

        <FormField
          id="lead-message"
          label={t("leadForm.fields.message")}
          error={errors.message ? t(errors.message) : undefined}
          hint={t("leadForm.fields.messageCounter", {
            count: values.message.length,
            max: MESSAGE_MAX_LENGTH,
          })}
        >
          <textarea
            ref={(el) => {
              fieldRefs.current.message = el;
            }}
            name="message"
            rows={4}
            maxLength={MESSAGE_MAX_LENGTH}
            value={values.message}
            disabled={submitting}
            onChange={(e) => {
              setField("message", e.target.value);
              clearFieldError("message");
            }}
            onBlur={() => handleBlur("message")}
          />
        </FormField>

        {/* Honeypot — real visitors never see or reach this (aria-hidden + not tab-reachable +
            off-screen, never display:none since some bots skip display:none fields). Misleading
            label ("website") targets automated fillers only. */}
        <div className="fa-lead-form__honeypot" aria-hidden="true">
          <label htmlFor="lead-website">Website</label>
          <input
            id="lead-website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="fa-checkbox-field">
          <input
            ref={(el) => {
              fieldRefs.current.consent = el;
            }}
            id="lead-consent"
            type="checkbox"
            checked={values.consent}
            disabled={submitting}
            aria-invalid={Boolean(errors.consent)}
            aria-describedby={errors.consent ? "lead-consent-error" : undefined}
            onChange={(e) => {
              setField("consent", e.target.checked);
              clearFieldError("consent");
            }}
            onBlur={() => handleBlur("consent")}
          />
          <label htmlFor="lead-consent">
            {t("leadForm.fields.consentPrefix")}{" "}
            <a
              href={`/${lang}/privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("leadForm.fields.consentLinkText")}
            </a>
            <span className="fa-field__required" aria-hidden="true">
              {" "}
              *
            </span>
          </label>
        </div>
        {errors.consent && (
          <p id="lead-consent-error" className="fa-field__error" role="alert">
            {t(errors.consent)}
          </p>
        )}

        <button
          type="submit"
          className="fa-btn fa-btn--primary fa-btn--lg fa-lead-form__submit"
          disabled={submitting}
        >
          {submitting && (
            <span className="fa-btn__spinner" aria-hidden="true" />
          )}
          {submitting ? t("leadForm.submitting") : t("leadForm.submit")}
        </button>

        <p className="fa-lead-form__captcha-badge">
          {CAPTCHA_PROVIDER === "recaptcha"
            ? t("leadForm.captchaBadgeReal")
            : t("leadForm.captchaBadgeDev")}
        </p>
      </form>
    </div>
  );
}
